import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const SupportTicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [updateStatus, setUpdateStatus] = useState('');
  const [updatePriority, setUpdatePriority] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTicket();
  }, [id]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      const response = await api.getSupportTicketById(id);
      if (response.success) {
        setTicket(response.data);
        setUpdateStatus(response.data.status);
        setUpdatePriority(response.data.priority);
        setAssignedTo(response.data.assigned_to || '');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const updateData = {};
      if (updateStatus !== ticket.status) {
        updateData.status = updateStatus;
      }
      if (updatePriority !== ticket.priority) {
        updateData.priority = updatePriority;
      }
      if (assignedTo !== ticket.assigned_to) {
        updateData.assigned_to = assignedTo || null;
      }

      if (Object.keys(updateData).length > 0) {
        await api.updateSupportTicket(id, updateData);
        loadTicket();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim()) {
      return;
    }

    try {
      setSaving(true);
      await api.addTicketComment(id, comment, updateStatus, updatePriority);
      setComment('');
      loadTicket();
    } catch (error) {
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const blob = await api.downloadAttachment(attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setError('Failed to download attachment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: 'bg-primary',
      in_progress: 'bg-info',
      pending_customer: 'bg-warning',
      resolved: 'bg-success',
      closed: 'bg-secondary',
    };
    return badges[status] || 'bg-secondary';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: 'bg-secondary',
      medium: 'bg-info',
      high: 'bg-warning',
      urgent: 'bg-danger',
    };
    return badges[priority] || 'bg-secondary';
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  if (!ticket) {
    return <div className="alert alert-danger">Ticket not found</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-12">
          <button className="btn btn-default btn-sm" onClick={() => navigate('/support-tickets')}>
            <i className="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <code>{ticket.ticket_number}</code> - {ticket.subject}
              </h3>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Status:</strong>
                  <span className={`badge ${getStatusBadge(ticket.status)} ml-2`}>
                    {ticket.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="col-md-6">
                  <strong>Priority:</strong>
                  <span className={`badge ${getPriorityBadge(ticket.priority)} ml-2`}>
                    {ticket.priority.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <strong>Description:</strong>
                <div className="border p-3 mt-2" style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f9f9f9' }}>
                  {ticket.description}
                </div>
              </div>

              {ticket.resolution_notes && (
                <div className="mb-3">
                  <strong>Resolution Notes:</strong>
                  <div className="border p-3 mt-2 bg-success text-white">
                    {ticket.resolution_notes}
                  </div>
                </div>
              )}

              {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="mb-3">
                  <strong>Attachments:</strong>
                  <ul className="list-group mt-2">
                    {ticket.attachments.map(attachment => (
                      <li key={attachment.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <span>
                          <i className="fas fa-paperclip mr-2"></i>
                          {attachment.file_name}
                          {attachment.file_size && (
                            <small className="text-muted ml-2">
                              ({(attachment.file_size / 1024).toFixed(2)} KB)
                            </small>
                          )}
                        </span>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                        >
                          <i className="fas fa-download"></i> Download
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <strong>Add Comment/Update:</strong>
                <textarea
                  className="form-control mt-2"
                  rows="4"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Enter your comment or update..."
                />
                <button
                  className="btn btn-primary btn-sm mt-2"
                  onClick={handleAddComment}
                  disabled={saving || !comment.trim()}
                >
                  {saving ? 'Saving...' : 'Add Comment'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Ticket Details</h3>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label>Status:</label>
                <select
                  className="form-control"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending_customer">Pending Customer</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              <div className="mb-3">
                <label>Priority:</label>
                <select
                  className="form-control"
                  value={updatePriority}
                  onChange={(e) => setUpdatePriority(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div className="mb-3">
                <label>Assigned To:</label>
                <input
                  type="text"
                  className="form-control"
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  placeholder="User ID (optional)"
                />
                {ticket.assigned_to_name && (
                  <small className="text-muted">Currently: {ticket.assigned_to_name}</small>
                )}
              </div>

              <button
                className="btn btn-success btn-block"
                onClick={handleUpdate}
                disabled={saving}
              >
                {saving ? 'Updating...' : 'Update Ticket'}
              </button>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <h3 className="card-title">Customer Information</h3>
            </div>
            <div className="card-body">
              <p>
                <strong>Name:</strong><br />
                {ticket.customer_name}
              </p>
              <p>
                <strong>Email:</strong><br />
                {ticket.customer_email}
              </p>
              {ticket.customer_phone && (
                <p>
                  <strong>Phone:</strong><br />
                  {ticket.customer_phone}
                </p>
              )}
              <p>
                <strong>License Key:</strong><br />
                <code style={{ fontSize: '11px' }}>{ticket.license_key}</code>
              </p>
              {ticket.company_name && (
                <p>
                  <strong>Company:</strong><br />
                  {ticket.company_name}
                </p>
              )}
              {ticket.category && (
                <p>
                  <strong>Category:</strong><br />
                  {ticket.category}
                </p>
              )}
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <h3 className="card-title">Timeline</h3>
            </div>
            <div className="card-body">
              <p>
                <strong>Created:</strong><br />
                {formatDate(ticket.created_at)}
              </p>
              <p>
                <strong>Updated:</strong><br />
                {formatDate(ticket.updated_at)}
              </p>
              {ticket.resolved_at && (
                <p>
                  <strong>Resolved:</strong><br />
                  {formatDate(ticket.resolved_at)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportTicketDetails;

